import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Navigation } from 'lucide-react';
import TopBar from '../components/common/TopBar';
import BottomNav from '../components/common/BottomNav';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useLocation } from '../contexts/LocationContext';
import { kioskService, beachService } from '../services/api';
import { toast } from 'sonner';

const MapView = () => {
  const navigate = useNavigate();
  const { location, requestLocation, hasLocation } = useLocation();
  const [kiosks, setKiosks] = useState([]);
  const [beaches, setBeaches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [location]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [kiosksData, beachesData] = await Promise.all([
        location
          ? kioskService.getNearby(location.latitude, location.longitude, 5000)
          : kioskService.list(null, true),
        beachService.list(),
      ]);
      setKiosks(kiosksData);
      setBeaches(beachesData);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="map-page">
      <TopBar title="Mapa" />

      <div className="max-w-md mx-auto">
        {/* Map placeholder area */}
        <div className="relative h-64 bg-gradient-to-br from-secondary/30 to-primary/30 overflow-hidden">
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6">
            <MapPin className="w-16 h-16 text-primary mb-3" strokeWidth={2} />
            <p className="font-['Outfit'] text-lg font-bold text-foreground">
              Mapa Interativo
            </p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">
              {hasLocation 
                ? 'Vendo quiosques próximos' 
                : 'Ative a localização para ver quiosques perto de você'}
            </p>
            {!hasLocation && (
              <button
                data-testid="enable-location-map"
                onClick={requestLocation}
                className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-full font-semibold flex items-center gap-2 active:scale-95"
              >
                <Navigation className="w-4 h-4" />
                Ativar localização
              </button>
            )}
          </div>
          {/* Decorative dots representing locations */}
          {kiosks.length > 0 && (
            <>
              <div className="absolute top-12 left-1/3 w-4 h-4 bg-primary rounded-full animate-pulse shadow-lg" />
              <div className="absolute top-24 right-1/4 w-4 h-4 bg-secondary rounded-full animate-pulse shadow-lg" />
              <div className="absolute bottom-16 left-1/2 w-4 h-4 bg-primary rounded-full animate-pulse shadow-lg" />
            </>
          )}
        </div>

        <div className="p-4 space-y-4">
          {/* Beaches selector */}
          {beaches.length > 0 && (
            <div>
              <h2 className="font-['Outfit'] font-bold text-lg mb-3">Praias</h2>
              <div className="flex gap-2 overflow-x-auto pb-2">
                {beaches.map((beach) => (
                  <button
                    key={beach.id}
                    data-testid={`beach-${beach.id}`}
                    className="px-4 py-2 bg-white border border-border rounded-full whitespace-nowrap font-semibold text-sm hover:border-primary transition-colors"
                  >
                    🏖️ {beach.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Kiosks list */}
          <div>
            <h2 className="font-['Outfit'] font-bold text-lg mb-3">
              {hasLocation ? 'Quiosques Próximos' : 'Quiosques Disponíveis'}
            </h2>
            {loading ? (
              <LoadingSpinner />
            ) : kiosks.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-xl border border-border">
                <p className="text-muted-foreground">Nenhum quiosque encontrado</p>
              </div>
            ) : (
              <div className="space-y-3">
                {kiosks.map((kiosk) => (
                  <button
                    key={kiosk.id}
                    data-testid={`map-kiosk-${kiosk.id}`}
                    onClick={() => navigate(`/kiosk/${kiosk.id}`)}
                    className="w-full bg-white rounded-xl shadow-sm border border-border p-4 text-left hover:shadow-md transition-shadow active:scale-[0.98] flex items-center gap-3"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <span className="text-2xl">🏖️</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">
                        {kiosk.display_name}
                      </h3>
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {kiosk.description}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-secondary flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          Raio: {kiosk.service_radius}m
                        </span>
                        {kiosk.is_open && (
                          <span className="text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full font-semibold">
                            Aberto
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default MapView;
